import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RemoveIcon from '@mui/icons-material/Remove';
import { Button, Collapse, Divider, Paper, Theme, Typography, useTheme } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import Lottie from 'lottie-react';
import { useState } from 'react';
import errorAnimation from '../../assets/plinks-error.json';
import { currencyHelper } from '../../helpers/currencyHelper';
import { errorHelper } from '../../helpers/errorHelper';
import { numberHelper } from '../../helpers/numberHelper';
import { Currency, Status } from '../../store/config/enums';
import variables from '../../styles/_variables.module.scss';
import { CustomSnackbar } from '../CustomSnackbar';
import { LandingLayout } from '../Landing';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paper: {
      padding: '1.25rem',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: '1rem',
      background: variables.white,
      maxWidth: '34.375rem',
      boxShadow: variables.boxShadow,
      border: `1px solid ${variables.borderButtonSecondary}`,
      [theme.breakpoints.down('md')]: {
        padding: '1rem',
      },
    },
    banner: {
      backgroundColor: variables.primaryShade,
      border: `1px solid ${variables.primary}`,
      borderRadius: '0.75rem',
      padding: '1rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
    },
    bannerTitle: {
      color: variables.deepBlue,
      fontWeight: Number(variables.semiBoldTextWeight),
    },
    bannerText: {
      margin: 0,
      color: variables.deepBlue,
      fontSize: '0.9375rem',
      lineHeight: 1.5,
    },
    detailMessage: {
      margin: 0,
      color: variables.deepBlue,
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    referenceBox: {
      display: 'flex',
      justifyContent: 'space-between',
      gap: '1rem',
      padding: '1rem',
      borderRadius: '0.75rem',
      backgroundColor: variables.lightGrey,
      border: `1px solid ${variables.borderButtonSecondary}`,
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
      },
    },
    referenceContent: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.375rem',
      minWidth: 0,
    },
    referenceLabel: {
      color: variables.slateBlue,
      fontWeight: Number(variables.semiBoldTextWeight),
    },
    referenceValue: {
      color: variables.deepBlue,
      fontWeight: Number(variables.semiBoldTextWeight),
      wordBreak: 'break-word',
      fontSize: '1.125rem',
      lineHeight: 1.4,
    },
    helperText: {
      color: variables.slateBlue,
      margin: 0,
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    section: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.875rem',
    },
    sectionTitle: {
      color: variables.deepBlue,
      fontWeight: Number(variables.semiBoldTextWeight),
      [theme.breakpoints.down('sm')]: {
        textAlign: 'center',
      },
    },
    detailRows: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.875rem',
    },
    detailRow: {
      display: 'flex',
      justifyContent: 'space-between',
      gap: '1rem',
      alignItems: 'flex-start',
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
        gap: '0.25rem',
        alignItems: 'center',
      },
    },
    detailLabel: {
      color: variables.slateBlue,
      fontWeight: Number(variables.semiBoldTextWeight),
      minWidth: '9rem',
      [theme.breakpoints.down('sm')]: {
        minWidth: 'auto',
        textAlign: 'center',
      },
    },
    detailValue: {
      color: variables.deepBlue,
      textAlign: 'right',
      wordBreak: 'break-word',
      [theme.breakpoints.down('sm')]: {
        textAlign: 'center',
      },
    },
    actions: {
      display: 'flex',
      gap: '0.75rem',
      [theme.breakpoints.down('sm')]: {
        flexDirection: 'column',
      },
    },
    actionButton: {
      [theme.breakpoints.down('sm')]: {
        width: '100%',
      },
    },
    stepsList: {
      margin: 0,
      paddingLeft: '1.25rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem',
      color: variables.deepBlue,
      '& li::marker': {
        color: variables.primary,
      },
    },
    stepItem: {
      paddingLeft: '0.25rem',
      lineHeight: 1.5,
      color: variables.deepBlue,
    },
    technicalToggle: {
      alignSelf: 'flex-start',
      paddingLeft: 0,
      paddingRight: 0,
      minWidth: 0,
      [theme.breakpoints.down('sm')]: {
        width: '100%',
        justifyContent: 'space-between',
      },
    },
    technicalBox: {
      marginTop: '0.75rem',
      padding: '1rem',
      borderRadius: '0.75rem',
      backgroundColor: variables.lightGrey,
      border: `1px solid ${variables.borderButtonSecondary}`,
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem',
    },
    technicalText: {
      color: variables.slateBlue,
      lineHeight: 1.5,
      wordBreak: 'break-word',
    },
    validationList: {
      margin: 0,
      paddingLeft: '1.25rem',
      display: 'flex',
      flexDirection: 'column',
      gap: '0.5rem',
      color: variables.slateBlue,
    },
  }),
);

interface PaymentErrorCardProps {
  paymentReference?: string;
  businessName?: string;
  linkDescription?: string;
  amountValue?: number;
  currency?: Currency;
  issuerName?: string;
  installments?: number;
  linkReference?: string;
  technicalMessages?: string[];
  errorCode?: number;
  validationErrors?: string[];
}

function normalizeMessage(message?: string) {
  return message ? message.trim() : '';
}

function isGenericMessage(message?: string) {
  const normalizedMessage = normalizeMessage(message).toLowerCase();

  return (
    normalizedMessage === '' ||
    normalizedMessage === 'ha ocurrido un error inesperado' ||
    normalizedMessage === 'error desconocido' ||
    normalizedMessage === 'pago fallido'
  );
}

function removeDuplicateStrings(values: string[]) {
  return values.filter((value, index) => values.indexOf(value) === index);
}

function formatInstallments(installments?: number) {
  if (!installments) {
    return undefined;
  }

  return installments === 1 ? '1 cuota' : `${installments} cuotas`;
}

function PaymentErrorCard({
  paymentReference,
  businessName,
  linkDescription,
  amountValue,
  currency,
  issuerName,
  installments,
  linkReference,
  technicalMessages = [],
  errorCode,
  validationErrors = [],
}: PaymentErrorCardProps) {
  const classes = useStyles(useTheme());
  const [technicalDetailsOpen, setTechnicalDetailsOpen] = useState(false);
  const [snackbarState, setSnackbarState] = useState({
    open: false,
    message: '',
    type: Status.SUCCESS,
  });

  const mappedErrorMessage =
    errorCode && errorCode > 0
      ? errorHelper.getErrorString(errorCode, normalizeMessage(technicalMessages[0]))
      : '';

  const normalizedMessages = removeDuplicateStrings(
    technicalMessages.map((message) => normalizeMessage(message)).filter((message) => message !== ''),
  );

  const visibleMessages = removeDuplicateStrings(
    [mappedErrorMessage, ...normalizedMessages].filter((message) => !isGenericMessage(message)),
  );

  const primaryDetailMessage = visibleMessages[0];
  const amountText =
    amountValue !== undefined && currency !== undefined
      ? `${currencyHelper.getCurrencyToken(currency)} ${numberHelper.getNumberWithDots(amountValue)}`
      : undefined;

  const detailRows = [
    businessName ? { label: 'Comercio', value: businessName } : null,
    linkDescription ? { label: 'Concepto', value: linkDescription } : null,
    amountText ? { label: 'Importe', value: amountText } : null,
    issuerName ? { label: 'Medio de pago', value: issuerName } : null,
    installments ? { label: 'Cuotas', value: formatInstallments(installments)! } : null,
    linkReference ? { label: 'Referencia del link', value: linkReference } : null,
  ].filter((row): row is { label: string; value: string } => row !== null);

  const supportMessageLines = ['Hola. Intenté pagar este link y el resultado no pudo ser confirmado.'];

  if (businessName) {
    supportMessageLines.push(`Comercio: ${businessName}`);
  }

  if (linkDescription) {
    supportMessageLines.push(`Concepto: ${linkDescription}`);
  }

  if (amountText) {
    supportMessageLines.push(`Importe: ${amountText}`);
  }

  if (paymentReference) {
    supportMessageLines.push(`Referencia de pago: ${paymentReference}`);
  }

  if (primaryDetailMessage) {
    supportMessageLines.push(`Detalle informado: ${primaryDetailMessage}`);
  }

  const hasTechnicalDetails = Boolean(errorCode && errorCode > 0) || visibleMessages.length > 0 || validationErrors.length > 0;

  const handleSnackbarClose = () => {
    setSnackbarState((currentState) => ({
      ...currentState,
      open: false,
    }));
  };

  const copyText = async (text: string, successMessage: string) => {
    try {
      if (!navigator.clipboard) {
        throw new Error('Clipboard unavailable');
      }

      await navigator.clipboard.writeText(text);
      setSnackbarState({
        open: true,
        message: successMessage,
        type: Status.SUCCESS,
      });
    } catch (_error) {
      setSnackbarState({
        open: true,
        message: 'No pudimos copiar la información. Intenta nuevamente.',
        type: Status.ERROR,
      });
    }
  };

  const animation = <Lottie animationData={errorAnimation} loop={false} />;

  return (
    <div className="screen-container landing">
      <LandingLayout
        title={'¡ATENCIÓN!\nNo pudimos confirmar tu pago'}
        titleColor={variables.deepBlue}
        animationDesktopOffsetTop="6.6875rem"
        animationMobileOffsetTop="2rem"
        subtitle=""
        animation={animation}
        content={
          <Paper className={classes.paper}>
            {primaryDetailMessage && (
              <div className={classes.banner}>
                  <p className={classes.detailMessage}>
                    <strong>Detalle informado:</strong> {primaryDetailMessage}
                  </p>
              </div>
            )}
            <div className={classes.referenceBox}>
              <div className={classes.referenceContent}>
                <Typography variant="subtitle2" className={classes.referenceLabel}>
                  Referencia de pago
                </Typography>
                <Typography variant="h6" className={classes.referenceValue}>
                  {paymentReference || 'No disponible'}
                </Typography>
                <p className={classes.helperText}>
                  Antes de intentar pagar nuevamente, copia la referencia haciendo click en el botón verde, y envíala a quien te mandó el link.
                </p>
              </div>
              {paymentReference && (
                <Button
                  variant="contained"
                  color="primary"
                  className={classes.actionButton}
                  startIcon={<ContentCopyIcon fontSize="small" />}
                  onClick={() => copyText(paymentReference, 'Referencia copiada correctamente')}
                >
                  Copiar referencia
                </Button>
              )}
            </div>

            {detailRows.length > 0 && (
              <>
                <Divider />
                <div className={classes.section}>
                  <Typography variant="subtitle2" className={classes.sectionTitle}>
                    Datos útiles
                  </Typography>
                  <div className={classes.detailRows}>
                    {detailRows.map((row) => (
                      <div className={classes.detailRow} key={row.label}>
                        <Typography variant="subtitle2" className={classes.detailLabel}>
                          {row.label}
                        </Typography>
                        <Typography variant="body1" className={classes.detailValue}>
                          {row.value}
                        </Typography>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            <Divider />

            <div className={classes.section}>
              <Typography variant="subtitle2" className={classes.sectionTitle}>
                El motivo del posible error puede ser diverso según el emisor o el banco:
              </Typography>
              <ul className={classes.stepsList}>
                <li className={classes.stepItem}>
                  Sin fondos disponibles.
                </li>
                <li className={classes.stepItem}>
                  Bloqueo antifraude.
                </li>
                <li className={classes.stepItem}>
                  Límite de compra excedido.
                </li>
                <li className={classes.stepItem}>
                  Datos de la tarjeta incorrectos, vencida o deshabilitada.
                </li>
              </ul>
            </div>

            {hasTechnicalDetails && (
              <div>
                <Button
                  color="secondary"
                  className={classes.technicalToggle}
                  endIcon={technicalDetailsOpen ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
                  onClick={() => setTechnicalDetailsOpen((isOpen) => !isOpen)}
                >
                  {technicalDetailsOpen ? 'Ocultar detalle para soporte' : 'Ver detalle para soporte'}
                </Button>
                <Collapse in={technicalDetailsOpen}>
                  <div className={classes.technicalBox}>
                    {errorCode && errorCode > 0 && (
                      <Typography variant="body2" className={classes.technicalText}>
                        <strong>Código de error:</strong> {errorCode}
                      </Typography>
                    )}
                    {visibleMessages.map((message) => (
                      <Typography key={message} variant="body2" className={classes.technicalText}>
                        <strong>Detalle:</strong> {message}
                      </Typography>
                    ))}
                    {validationErrors.length > 0 && (
                      <>
                        <Typography variant="body2" className={classes.technicalText}>
                          <strong>Validaciones:</strong>
                        </Typography>
                        <ul className={classes.validationList}>
                          {validationErrors.map((validationError) => (
                            <li key={validationError}>{validationError}</li>
                          ))}
                        </ul>
                      </>
                    )}
                  </div>
                </Collapse>
              </div>
            )}
          </Paper>
        }
      />
      <CustomSnackbar
        open={snackbarState.open}
        message={snackbarState.message}
        handleClose={handleSnackbarClose}
        type={snackbarState.type}
      />
    </div>
  );
}

export default PaymentErrorCard;
